/**
 * Step 5: Adaptive ServiceCards. Fetch /api/ux-services avec timeout 150ms, cache 60s.
 * Fallback ordre par défaut si flags OFF, hors rollout, timeout ou erreur.
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";
import { UX_SESSION_STORAGE_KEY } from "@/lib/uxTracking";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 60_000;

export const DEFAULT_SERVICE_ORDER = ["vision", "dental", "hair", "aesthetic"] as const;
export type ServiceId = "vision" | "dental" | "hair" | "aesthetic";
export type ServicesMicrocopyVariant = "trust" | "fast" | "neutral";

export interface AdaptiveServicesResult {
  order: ServiceId[];
  spotlight?: ServiceId;
  microcopyVariant?: ServicesMicrocopyVariant;
  isAdaptive: boolean;
}

interface ServicesCacheEntry {
  data: AdaptiveServicesResult;
  expiresAt: number;
}

let memoryCache: { sessionId: string; entry: ServicesCacheEntry } | null = null;

function getCached(sessionId: string): AdaptiveServicesResult | null {
  if (!memoryCache || memoryCache.sessionId !== sessionId || Date.now() >= memoryCache.entry.expiresAt) {
    return null;
  }
  return memoryCache.entry.data;
}

function setCached(sessionId: string, data: AdaptiveServicesResult): void {
  memoryCache = {
    sessionId,
    entry: { data, expiresAt: Date.now() + CACHE_TTL_MS },
  };
}

const FALLBACK: AdaptiveServicesResult = {
  order: [...DEFAULT_SERVICE_ORDER],
  isAdaptive: false,
};

export function useAdaptiveServices(): AdaptiveServicesResult {
  const [result, setResult] = useState<AdaptiveServicesResult>(FALLBACK);
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const sessionId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UX_SESSION_STORAGE_KEY) : null;

    const applyFallback = () => {
      if (!cancelled) setResult(FALLBACK);
    };

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxServicesEnabled || !sessionId) {
        applyFallback();
        return;
      }

      const cached = getCached(sessionId);
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(`/api/ux-services?session_id=${encodeURIComponent(sessionId)}`, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) {
          applyFallback();
          return;
        }
        const data = (await r.json()) as {
          order: ServiceId[];
          spotlight?: ServiceId;
          microcopyVariant?: ServicesMicrocopyVariant;
        };
        const next: AdaptiveServicesResult = {
          order: Array.isArray(data.order) && data.order.length > 0 ? data.order : [...DEFAULT_SERVICE_ORDER],
          spotlight: data.spotlight,
          microcopyVariant: data.microcopyVariant,
          isAdaptive: true,
        };
        setCached(sessionId, next);
        setResult(next);
      } catch {
        if (!cancelled) applyFallback();
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
